import { QueryClientProvider } from '@tanstack/react-query'
import { DemoExternalOrder } from 'app/src/features/DemoApp/components/DemoExternalOrder'
import { Favourites } from 'app/src/features/Favourites'
import { MealVouchersContainer } from 'app/src/features/MealVouchers/MealVouchersContainer'
import { Proposals } from 'app/src/features/Proposals'
import {
  GoodAndService,
  MultiVoucher,
  MultiVoucherUsage,
  Refund,
  TransferFondoPensione,
  TransferFondoSalute,
} from 'app/src/features/Shopping'
import { GiftCard } from 'app/src/features/Shopping/Direct/Giftcard'
import { MultiVoucherUsageDetail } from 'app/src/features/Shopping/Direct/MultiVoucherUsageDetail'
import { Voucher } from 'app/src/features/Shopping/Direct/Voucher'
import { Discount } from 'app/src/features/Shopping/Discount'
import { ShowcaseContainer } from 'app/src/features/Showcase/ShowcaseContainer'
import { Tutorial } from 'app/src/features/Tutorial'
import { UserAccount } from 'app/src/features/UserAccount'
import { UserAccountOrderDetailDialog } from 'app/src/features/UserAccount/components/UserAccountOrderDetailDialog'
import { UserJoin } from 'app/src/features/UserJoin'
import { I18nContextProvider, LocaleManager, UnrecoverableError } from 'app/src/shared/components'
import { ptOptions, queryClient } from 'app/src/shared/helpers'
import { NavigatePaths, useWebAppContext } from 'app/src/shared/hooks'
import { PrimeReactProvider } from 'primereact/api'
import { useMemo } from 'react'
import { createHashRouter, RouterProvider, type RouteObject } from 'react-router-dom'
import { Accessibility, Catalog, Home, Landing, NewsDetail, NewsList, UserInfo } from './features'

function App() {
  const context = useWebAppContext()
  const router = useMemo(() => {
    const children: RouteObject[] | undefined = []

    if (context.userInfo.isForbidden) {
      children.push({
        path: NavigatePaths.Any,
        element: <></>,
        errorElement: <UnrecoverableError />,
      })
    } else if (context.userInfo.canSelfRegister) {
      children.push({
        path: NavigatePaths.Any,
        element: <></>,
        errorElement: <UnrecoverableError />,
      })
    } else {
      children.push(
        {
          index: true,
          element: <Landing />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.Catalog,
          element: <Catalog />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.Proposals,
          element: <Proposals />,
        },
        {
          path: NavigatePaths.News,
          element: <NewsList />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.NewsDetail,
          element: <NewsDetail />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.Showcase,
          element: <ShowcaseContainer />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.Favourites,
          element: <Favourites />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.UserInfo,
          element: <UserInfo />,
          errorElement: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.Tutorial,
          element: <Tutorial />,
        },
        {
          path: NavigatePaths.UserJoin,
          element: <UserJoin />,
        },
        {
          path: NavigatePaths.GiftCard,
          element: <GiftCard />,
        },
        {
          path: NavigatePaths.DiscountDetail,
          element: <Discount />,
        },
        {
          path: NavigatePaths.MultiVoucher,
          element: <MultiVoucher />,
        },
        {
          path: NavigatePaths.Voucher,
          element: <Voucher />,
        },
        {
          path: NavigatePaths.GoodAndService,
          element: <GoodAndService />,
        },
        {
          path: NavigatePaths.Refund,
          element: <Refund />,
        },
        {
          path: NavigatePaths.MultiVoucherUsage,
          element: <MultiVoucherUsage />,
        },
        {
          path: NavigatePaths.MultiVoucherUsageDetail,
          element: <MultiVoucherUsageDetail />,
        },
        {
          path: NavigatePaths.TransferFondoPensione,
          element: <TransferFondoPensione />,
        },
        {
          path: NavigatePaths.TransferFondoSalute,
          element: <TransferFondoSalute />,
        },
        {
          path: NavigatePaths.Error,
          element: <UnrecoverableError />,
        },
        {
          path: NavigatePaths.MealVouchers,
          element: <MealVouchersContainer />,
        },
        {
          path: NavigatePaths.DemoExternalOrder,
          element: <DemoExternalOrder />,
        },
      )

      children.push({
        path: NavigatePaths.UserAccount,
        element: <UserAccount />,
        errorElement: <UnrecoverableError />,
        children: [
          {
            path: NavigatePaths.UserAccount + NavigatePaths.GiftCard,
            element: (
              <UserAccountOrderDetailDialog>
                <GiftCard />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.DiscountDetail,
            element: (
              <UserAccountOrderDetailDialog>
                <Discount />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.MultiVoucher,
            element: (
              <UserAccountOrderDetailDialog>
                <MultiVoucher />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.Voucher,
            element: (
              <UserAccountOrderDetailDialog>
                <Voucher />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.GoodAndService,
            element: (
              <UserAccountOrderDetailDialog>
                <GoodAndService />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.Refund,
            element: (
              <UserAccountOrderDetailDialog>
                <Refund />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.MultiVoucherUsage,
            element: (
              <UserAccountOrderDetailDialog>
                <MultiVoucherUsage />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.MultiVoucherUsageDetail,
            element: (
              <UserAccountOrderDetailDialog>
                <MultiVoucherUsageDetail />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.TransferFondoPensione,
            element: (
              <UserAccountOrderDetailDialog>
                <TransferFondoPensione />
              </UserAccountOrderDetailDialog>
            ),
          },
          {
            path: NavigatePaths.UserAccount + NavigatePaths.TransferFondoSalute,
            element: (
              <UserAccountOrderDetailDialog>
                <TransferFondoSalute />
              </UserAccountOrderDetailDialog>
            ),
          },
        ],
      })

      children.push({
        path: NavigatePaths.Any,
        element: <Landing />,
        errorElement: <UnrecoverableError />,
      })
    }

    return createHashRouter([
      {
        path: NavigatePaths.Root,
        id: 'root',
        element: <Home />,
        errorElement: <UnrecoverableError />,
        children,
      },
      {
        path: NavigatePaths.Accessibility,
        element: <Accessibility />,
        errorElement: <UnrecoverableError />,
      },
    ])
  }, [context.userInfo.isForbidden, context.userInfo.canSelfRegister])

  return (
    <PrimeReactProvider value={ptOptions}>
      <QueryClientProvider client={queryClient}>
        <I18nContextProvider>
          <LocaleManager>
            <RouterProvider router={router} />
          </LocaleManager>
        </I18nContextProvider>
      </QueryClientProvider>
    </PrimeReactProvider>
  )
}

export default App
